import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap, Maximize, Check, X, Package, Truck, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMachineBySlug } from '../services/apiService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { parseModels, powerRange, areaRange } from '../utils/machineModels';

const withMm = (v) => (/^\d+([.,]\d+)?$/.test(String(v).trim()) ? `${v} mm` : v);

const TYPE_LABELS = { co2: 'Laser CO2', fiber: 'Laser Fibre', mixed: 'Laser Mixte' };

const ModelsTable = ({ models }) => (
  <div className="mb-8 rounded-xl overflow-hidden border shadow-sm" style={{ borderColor: '#E8D9C4' }} data-testid="machine-models-table">
    <div className="px-5 py-3 font-bold text-white" style={{ backgroundColor: '#5C3A21' }}>
      Modèles disponibles ({models.length})
    </div>
    <div className="max-h-96 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0" style={{ backgroundColor: '#F5EBDD' }}>
          <tr style={{ color: '#5C3A21' }}>
            <th className="px-4 py-2 text-left font-semibold">Modèle</th>
            <th className="px-4 py-2 text-left font-semibold">Puissance</th>
            <th className="px-4 py-2 text-left font-semibold">Zone de travail</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`machine-model-row-${idx}`}>
              <td className="px-4 py-2 font-medium text-gray-900">{m.name}</td>
              <td className="px-4 py-2 text-gray-700">{m.power || '—'}</td>
              <td className="px-4 py-2 text-gray-700">{m.area || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const MetalCuttingTable = ({ specs }) => (
  <div className="p-6 bg-white rounded-xl shadow-sm" data-testid="machine-metal-cutting-table">
    <h3 className="font-bold mb-3" style={{ color: '#5C3A21' }}>Capacités de découpe métaux</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: '#5C3A21' }} className="text-white">
            <th className="px-4 py-2 text-left">Métal</th>
            <th className="px-4 py-2 text-left">Puissance</th>
            <th className="px-4 py-2 text-left">Épaisseur min</th>
            <th className="px-4 py-2 text-left">Épaisseur max</th>
          </tr>
        </thead>
        <tbody>
          {specs.map((s, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="px-4 py-2 font-medium text-gray-900">{s.metalType}</td>
              <td className="px-4 py-2 text-gray-700">{s.power}</td>
              <td className="px-4 py-2 text-gray-700">{withMm(s.minThickness)}</td>
              <td className="px-4 py-2 text-gray-700">{withMm(s.maxThickness)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Mapping des clés techniques vers les libellés français affichés
const MATERIAL_LABELS = {
  wood: 'Bois & dérivés (bambou, MDF, etc.)',
  acrylic: 'Acrylique',
  glass: 'Verre',
  paper: 'Papier',
  fabric: 'Tissu',
  leather: 'Cuir',
  rubber: 'Caoutchouc',
  painted_metal: 'Métal peint',
  metal: 'Métal',
  stone_marble: 'Pierre & Marbre',
  ceramic: 'Céramique',
};

const formatMaterialLabel = (raw) => {
  if (!raw) return '';
  const key = String(raw).trim().toLowerCase();
  return MATERIAL_LABELS[key] || raw;
};

const MachineDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMachine = async () => {
      try {
        setLoading(true);
        const data = await getMachineBySlug(slug);
        setMachine(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching machine:', err);
        setError('Machine non trouvée');
        setLoading(false);
      }
    };

    fetchMachine();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !machine) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#5C3A21' }}>Machine non trouvée</h1>
          <Link to="/machines" className="text-blue-600 hover:underline">Retour aux machines</Link>
        </div>
      </div>
    );
  }

  const models = parseModels(machine.models);
  const specs = machine.technicalSpecs || {};
  const metalSpecs = machine.metalCuttingSpecs || [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % machine.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + machine.gallery.length) % machine.gallery.length);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-gray-900">Accueil</Link>
            <span className="text-gray-400">/</span>
            <Link to="/machines" className="text-gray-600 hover:text-gray-900">Machines</Link>
            <span className="text-gray-400">/</span>
            <span className="font-semibold" style={{ color: '#5C3A21' }}>{machine.name}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/machines')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Retour aux machines
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Image Gallery */}
            <div>
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl mb-4 bg-gray-50 flex items-center justify-center">
                <img
                  src={machine.gallery[currentImageIndex]}
                  alt={machine.name}
                  className="w-full h-full object-contain p-3"
                />
                {machine.gallery.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-lg"
                    >
                      <ChevronLeft className="w-6 h-6" style={{ color: '#5C3A21' }} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-lg"
                    >
                      <ChevronRight className="w-6 h-6" style={{ color: '#5C3A21' }} />
                    </button>
                  </>
                )}
              </div>
              {machine.gallery.length > 1 && (
                <div className="flex gap-3">
                  {machine.gallery.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-1 aspect-video rounded-lg overflow-hidden border-2 transition-all bg-gray-50 flex items-center justify-center ${
                        currentImageIndex === idx ? 'border-amber-400' : 'border-gray-200 opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`${machine.name} ${idx + 1}`} className="w-full h-full object-contain p-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Machine Info */}
            <div>
              <div className="inline-block px-4 py-1 rounded-full text-sm font-semibold text-white mb-4" style={{ backgroundColor: '#5C3A21' }}>
                {TYPE_LABELS[machine.type] || 'Laser'}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
                {machine.name}
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed whitespace-pre-line">
                {machine.longDescription || machine.description}
              </p>

              {models.length > 0 && <ModelsTable models={models} />}

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-6 rounded-xl" style={{ backgroundColor: '#F5EBDD' }}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Zap className="w-5 h-5" style={{ color: '#5C3A21' }} />
                    <span className="font-semibold text-sm" style={{ color: '#5C3A21' }}>Puissance</span>
                  </div>
                  <p className="text-lg md:text-xl font-bold text-gray-900 break-words" data-testid="machine-power-range">{powerRange(models, machine.power)}</p>
                </div>
                <div className="p-6 rounded-xl" style={{ backgroundColor: '#F5EBDD' }}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Maximize className="w-5 h-5" style={{ color: '#5C3A21' }} />
                    <span className="font-semibold text-sm" style={{ color: '#5C3A21' }}>Zone de travail</span>
                  </div>
                  <p className="text-lg md:text-xl font-bold text-gray-900 break-words" data-testid="machine-area-range">{areaRange(models, machine.workArea)}</p>
                </div>
              </div>

              <div className="mb-8 p-6 rounded-xl border-2" style={{ borderColor: '#F5EBDD', backgroundColor: '#F5EBDD' }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-700">Prix indicatif :</span>
                  <span className="text-3xl font-bold" style={{ color: '#5C3A21' }}>{machine.price}</span>
                </div>
                <Link
                  to="/contact"
                  className="block w-full text-center px-8 py-4 rounded-xl text-white font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  Demander un devis personnalisé
                  <ArrowRight className="inline ml-2 w-5 h-5" />
                </Link>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Points forts :</h3>
                {machine.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detailed Tabs */}
          <Tabs defaultValue="specs" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 bg-transparent h-auto p-0 mb-8">
              <TabsTrigger value="specs" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Caractéristiques
              </TabsTrigger>
              <TabsTrigger value="equipment" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Équipements inclus
              </TabsTrigger>
              <TabsTrigger value="options" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Options
              </TabsTrigger>
              <TabsTrigger value="delivery" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Livraison
              </TabsTrigger>
              <TabsTrigger value="materials" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Matériaux
              </TabsTrigger>
              <TabsTrigger value="applications" className="data-[state=active]:bg-[#5C3A21] data-[state=active]:text-white rounded-lg py-3">
                Applications
              </TabsTrigger>
            </TabsList>

            <TabsContent value="specs" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>Caractéristiques techniques</h2>
                
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="machine-specs-summary">
                      <div className="p-6 bg-white rounded-xl shadow-sm">
                        <h3 className="font-bold mb-2" style={{ color: '#5C3A21' }}>Puissance</h3>
                        <p className="text-gray-700">{powerRange(models, machine.power)}</p>
                      </div>
                      <div className="p-6 bg-white rounded-xl shadow-sm">
                        <h3 className="font-bold mb-2" style={{ color: '#5C3A21' }}>Zone de travail</h3>
                        <p className="text-gray-700">{areaRange(models, machine.workArea)}</p>
                      </div>
                      {machine.tableHeight && (
                        <div className="p-6 bg-white rounded-xl shadow-sm" data-testid="machine-table-height">
                          <h3 className="font-bold mb-2" style={{ color: '#5C3A21' }}>Hauteur de table</h3>
                          <p className="text-gray-700">{machine.tableHeight}</p>
                        </div>
                      )}
                    </div>
                    {metalSpecs.length > 0 && <MetalCuttingTable specs={metalSpecs} />}
                    {specs.cooling && (
                      <div className="p-6 bg-white rounded-xl shadow-sm">
                        <h3 className="font-bold mb-2" style={{ color: '#5C3A21' }}>Refroidissement</h3>
                        <p className="text-gray-700">{specs.cooling}</p>
                      </div>
                    )}
                    {specs.voltage && (
                      <div className="p-6 bg-white rounded-xl shadow-sm">
                        <h3 className="font-bold mb-2" style={{ color: '#5C3A21' }}>Alimentation</h3>
                        <p className="text-gray-700">{specs.voltage}</p>
                      </div>
                    )}
                    {specs.tableTypes?.length > 0 && (
                    <div className="p-6 bg-white rounded-xl shadow-sm">
                      <h3 className="font-bold mb-3" style={{ color: '#5C3A21' }}>Types de tables fournies</h3>
                      <div className="space-y-3">
                        {specs.tableTypes.map((table, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                            <div>
                              <span className="font-semibold text-gray-900">{table.name} :</span>
                              <span className="text-gray-700"> {table.description}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    )}
                    {machine.warranty && (
                      <div className="p-6 bg-white rounded-xl shadow-sm">
                        <h3 className="font-bold mb-3" style={{ color: '#5C3A21' }}>Garantie</h3>
                        <div className="space-y-2">
                          <p className="text-gray-700"><span className="font-semibold">Machine :</span> {machine.warranty.machine}</p>
                          <p className="text-gray-700"><span className="font-semibold">Pièces d'usure :</span> {machine.warranty.parts}</p>
                        </div>
                      </div>
                    )}
                  </div>
              </div>
            </TabsContent>

            <TabsContent value="equipment" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                  <Package className="inline w-8 h-8 mr-2" />
                  Équipements inclus
                </h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Toutes nos machines sont livrées prêtes à l'emploi avec les équipements essentiels :
                </p>
                <div className="space-y-4">
                  {machine.includedEquipment && machine.includedEquipment.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <Check className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5C3A21' }} />
                      <p className="text-gray-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="options" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>Options disponibles</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Personnalisez votre machine selon vos besoins avec ces options supplémentaires :
                </p>
                {machine.availableOptions && machine.availableOptions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {machine.availableOptions.map((option, idx) => (
                      <div key={idx} className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5EBDD' }}>
                            <Check className="w-5 h-5" style={{ color: '#5C3A21' }} />
                          </div>
                          <p className="text-gray-700 leading-relaxed pt-2">{option}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 italic">Options à venir - Contactez-nous pour plus d'informations.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="delivery" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                  <Truck className="inline w-8 h-8 mr-2" />
                  Livraison et installation
                </h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Chez AKM Laser, on vous accompagne jusqu'au bout. Que vous soyez artisan, entreprise ou particulier, notre objectif est simple : vous livrer rapidement, installer proprement, et vous former efficacement.
                </p>
                
                <div className="space-y-6">
                  {machine.deliveryOptions && machine.deliveryOptions.map((option, idx) => (
                    <div key={idx} className="p-8 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="text-2xl font-bold mb-4" style={{ color: '#5C3A21' }}>
                        {idx + 1}. {option.title}
                      </h3>
                      <ul className="space-y-3">
                        {option.items.map((item, itemIdx) => (
                          <li key={itemIdx} className="flex items-start space-x-3">
                            {item.startsWith('•') ? (
                              <>
                                <span className="text-gray-400 mt-1">•</span>
                                <span className="text-gray-700">{item.substring(1).trim()}</span>
                              </>
                            ) : (
                              <>
                                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                                <span className="text-gray-700">{item}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-white rounded-xl border-2" style={{ borderColor: '#5C3A21' }}>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Chaque machine est testée, vérifiée, et calibrée avant livraison.</strong>
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="materials" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>Matériaux compatibles</h2>
                {machine.materialCompatibility && machine.materialCompatibility.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                      <thead>
                        <tr style={{ backgroundColor: '#5C3A21' }} className="text-white">
                          <th className="px-6 py-4 text-left font-bold">Matériau</th>
                          <th className="px-6 py-4 text-center font-bold">Gravure</th>
                          <th className="px-6 py-4 text-center font-bold">Découpe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {machine.materialCompatibility.map((material, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-4 font-medium text-gray-900">{formatMaterialLabel(material.material)}</td>
                            <td className="px-6 py-4 text-center">
                              {material.engrave ? (
                                <Check className="w-6 h-6 mx-auto text-green-600" />
                              ) : (
                                <X className="w-6 h-6 mx-auto text-red-500" />
                              )}
                            </td>
                            <td className="px-6 py-4 text-center">
                              {material.cut ? (
                                <Check className="w-6 h-6 mx-auto text-green-600" />
                              ) : (
                                <X className="w-6 h-6 mx-auto text-red-500" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {machine.materials.map((material, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 rounded-full text-sm font-medium bg-white shadow-md"
                        style={{ color: '#5C3A21' }}
                      >
                        {formatMaterialLabel(material)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="applications" className="mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>Applications et réalisations possibles</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Idéale pour les artisans, les créateurs, les industriels et toutes les entreprises cherchant à développer leur activité de gravure laser et de découpe laser.
                </p>
                {machine.applications && machine.applications.length > 0 && (
                  <div className="space-y-4 mb-8">
                    {machine.applications.map((application, idx) => (
                      <div key={idx} className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm">
                        <Check className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5C3A21' }} />
                        <p className="text-gray-700 leading-relaxed">{application}</p>
                      </div>
                    ))}
                  </div>
                )}

                {machine.youtubeVideo && (
                  <div className="mt-8">
                    <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: '#5C3A21' }}>
                      <Video className="w-6 h-6 mr-2" />
                      Découvrez la machine en vidéo
                    </h3>
                    <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${machine.youtubeVideo}`}
                        title="Démonstration machine"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      ></iframe>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* CTA Section */}
          <div className="mt-16 p-12 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-white text-center">
            <h2 className="text-4xl font-bold mb-4">Intéressé par cette machine ?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Contactez-nous pour un devis personnalisé ou pour réserver une démonstration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300"
                style={{ color: '#5C3A21' }}
              >
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/machines"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
              >
                Voir d'autres machines
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineDetail;
